import type { FC, ReactNode } from 'react'
import type { CategoryLink } from '@/types'

import Link from 'next/link'
import { Clock, MapPin, Phone } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { Container } from '@/components/generals/container'
import { contactInfo, mainNav, siteConfig } from '@/constants/site'

interface SiteFooterProps {
    categories: CategoryLink[]
}

export const SiteFooter: FC<SiteFooterProps> = ({ categories }): ReactNode => {
    return (
        <footer className='bg-foreground text-background'>
            <Container className='grid gap-12 py-16 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8 lg:py-20'>
                <div className='sm:col-span-2 lg:col-span-4'>
                    <Logo tone='light' />

                    <p className='mt-6 max-w-sm text-sm leading-8 text-background/70'>
                        نارون؛ مبلمان دست‌ساز برای خانه‌هایی که قرار است سال‌ها در آن زندگی کنید. هر قطعه را در کارگاه خودمان، از چوب خام تا روکش نهایی، با دقت می‌سازیم.
                    </p>
                </div>

                <nav aria-labelledby='footer-links' className='lg:col-span-2'>
                    <h2 id='footer-links' className='text-sm font-semibold text-background'>
                        دسترسی سریع
                    </h2>

                    <ul className='mt-5 flex flex-col gap-3 text-sm'>
                        {mainNav.map((link) => (
                            <li key={link.href}>
                                <Link href={link.href} className='text-background/70 transition-colors hover:text-background'>
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                {categories.length > 0 && (
                    <nav aria-labelledby='footer-categories' className='lg:col-span-2'>
                        <h2 id='footer-categories' className='text-sm font-semibold text-background'>
                            دسته‌بندی‌ها
                        </h2>

                        <ul className='mt-5 flex flex-col gap-3 text-sm'>
                            {categories.map((category) => (
                                <li key={category.id}>
                                    <Link href={`/collections/${category.slug}`} className='text-background/70 transition-colors hover:text-background'>
                                        <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                            {category.name}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>
                )}

                <div className='sm:col-span-2 lg:col-span-4'>
                    <h2 className='text-sm font-semibold text-background'>
                        شوروم نارون
                    </h2>

                    <ul className='mt-5 flex flex-col gap-4 text-sm text-background/70'>
                        <li className='flex items-start gap-3'>
                            <MapPin className='mt-1 size-4 shrink-0 text-background/50' aria-hidden='true' />
                            <a href={contactInfo.mapUrl} target='_blank' rel='noopener noreferrer' className='leading-7 transition-colors hover:text-background'>
                                {contactInfo.address}
                            </a>
                        </li>

                        <li className='flex items-start gap-3'>
                            <Phone className='mt-1 size-4 shrink-0 text-background/50' aria-hidden='true' />
                            <a href={contactInfo.phoneHref} className='leading-7 transition-colors hover:text-background'>
                                <span dir='ltr'>
                                    {contactInfo.phone}
                                </span>
                            </a>
                        </li>

                        <li className='flex items-start gap-3'>
                            <Clock className='mt-1 size-4 shrink-0 text-background/50' aria-hidden='true' />
                            <span className='leading-7'>
                                {contactInfo.hours}
                                <span className='block text-background/50'>
                                    {contactInfo.closedDays}
                                </span>
                            </span>
                        </li>
                    </ul>
                </div>
            </Container>

            <div className='border-t border-background/10'>
                <Container className='flex flex-col gap-2 py-6 text-xs text-background/50 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © {siteConfig.name}. تمامی حقوق محفوظ است.
                    </p>

                    <p>
                        ساخته‌شده با دقت، برای سال‌ها زندگی.
                    </p>
                </Container>
            </div>
        </footer>
    )
}
